// ConfirmPaidModal.js — LD-REV-004 (H5).
// Replaces the two window.prompt() calls that stood between a click and REAL payments rows
// on a Payment Request. Confirming a portion paid inserts a payments row per payor and, when
// every portion is confirmed, closes the linked bill as Paid — a terminal state. The modal
// names the payee and the exact dollars before that happens, so "clicked the adjacent button
// and pressed Enter through a date prompt" is no longer a way to spend $40,000.
//
// The status/threshold gate itself is the BACKEND's (payment_request_api._confirm_gate); this
// modal is the operator-facing half, never the wall.
import React, { useState, useEffect } from "react";
import { apiFetch, fmt } from "../api";

// fmt.money is the house money formatter (ATTICUS_DESIGN_STANDARD Part III).
const money = (v) => fmt.money(Number(v || 0));
const today = () => new Date().toISOString().slice(0, 10);

/**
 * props:
 *   requestId   — payment_requests.id
 *   recipientId — confirm ONE portion (optional; omitted = confirm all remaining)
 *   onClose()   — dismiss
 *   onConfirm(paidDate|null) — caller performs the POST
 *   busy        — caller's in-flight flag
 */
export default function ConfirmPaidModal({ requestId, recipientId = null, onClose, onConfirm, busy }) {
  const [pr, setPr] = useState(null);
  const [err, setErr] = useState(null);
  const [paidDate, setPaidDate] = useState(today());

  useEffect(() => {
    apiFetch(`/payment-requests/${requestId}`)
      .then(setPr)
      .catch(e => setErr(String(e.message || e).slice(0, 200)));
  }, [requestId]);

  const all = (pr && pr.recipients) || [];
  const target = recipientId
    ? all.filter(r => r.id === recipientId)
    : all.filter(r => !r.resulting_payment_id);
  const total = target.reduce((s, r) => s + Number(r.allocated_amount || 0), 0);
  const one = !!recipientId;
  const remainingAfter = one ? all.filter(r => !r.resulting_payment_id && r.id !== recipientId).length : 0;

  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal" style={{ maxWidth: 520 }}>
        <div className="modal-header">
          <h3>{one ? "Confirm this portion paid" : "Confirm all remaining portions paid"}</h3>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>
        <div className="modal-body">
          {err && <div className="alert-error">{err}</div>}
          {!pr && !err && <div style={{ fontSize: 12, color: "var(--text3)" }}>Loading…</div>}
          {pr && (
            <>
              <div style={{ fontSize: 12, color: "var(--text2)", marginBottom: 10 }}>
                This records {one ? "a real payment" : `${target.length} real payment${target.length === 1 ? "" : "s"}`} on
                claim <span className="mono" style={{ color: "var(--blue)", fontWeight: 700 }}>{pr.claim_number || `#${pr.claim_id}`}</span>
                {" "}— money the <b style={{ textTransform: "capitalize" }}>{pr.requested_payer}</b> paid the vendor directly.
                {!one && " Once every portion is confirmed the linked bill closes as Paid."}
              </div>

              <div style={{ background: "var(--bg3)", borderRadius: 6, padding: "10px 12px", marginBottom: 12 }}>
                <div style={{ display: "flex", justifyContent: "space-between", gap: 12, marginBottom: 6 }}>
                  <span style={{ fontSize: 11, color: "var(--text3)", textTransform: "uppercase", letterSpacing: 0.3 }}>Payee</span>
                  <span style={{ fontSize: 13, fontWeight: 600, textAlign: "right" }}>{pr.payee || "—"}</span>
                </div>
                {target.map(r => (
                  <div key={r.id} style={{ display: "flex", justifyContent: "space-between", gap: 12, fontSize: 12, padding: "2px 0" }}>
                    <span>{r.display_name || "Payor"}</span>
                    <span className="mono">{money(r.allocated_amount)}</span>
                  </div>
                ))}
                {target.length === 0 && <div style={{ fontSize: 12, color: "var(--text3)" }}>Nothing left to confirm on this request.</div>}
                <div style={{ display: "flex", justifyContent: "space-between", gap: 12, borderTop: "1px solid var(--border)", marginTop: 6, paddingTop: 6 }}>
                  <b style={{ fontSize: 12 }}>{one ? "Payment amount" : "Total to record"}</b>
                  <b className="mono" style={{ fontSize: 14 }}>{money(total)}</b>
                </div>
              </div>

              {one && remainingAfter > 0 && (
                <div style={{ fontSize: 11, color: "var(--text3)", marginBottom: 10 }}>
                  {remainingAfter} other portion{remainingAfter === 1 ? "" : "s"} will stay unpaid — the request becomes Partially Paid.
                </div>
              )}

              <div className="form-group" style={{ maxWidth: 200 }}>
                <label>Payment date</label>
                <input type="date" value={paidDate} onChange={e => setPaidDate(e.target.value)} />
              </div>
              <div style={{ fontSize: 11, color: "#92400e", marginTop: 10 }}>
                There is no in-app un-confirm yet — correcting this means an accounting fix.
              </div>
            </>
          )}
        </div>
        <div className="modal-footer">
          <button className="btn btn-outline" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" disabled={busy || !pr || target.length === 0 || !paidDate}
                  onClick={() => onConfirm(paidDate || null)}>
            {busy ? "Recording…" : `Record ${money(total)} paid`}
          </button>
        </div>
      </div>
    </div>
  );
}
